import { useEffect, useMemo, useRef, useState } from 'react';
import Point from '@arcgis/core/geometry/Point';
import * as locator from '@arcgis/core/rest/locator';
import { useFormContext } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import type { ResourceRequestItem } from 'components/IAP/hooks/incidentResourceRequestHooks';
import { useInternalLayersFeatures } from 'hooks/internalLayerVersion/useListInternalLayerVersionFeaturesQuery';
import { WGS84_SPATIAL_REFERENCE } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/spatialReference';

import { firstLatLng, parseLatLng } from './reportingLocation';

const GEOCODE_URL =
  'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer';

type AddressSuggestion = { text: string; magicKey: string };

type LocationChoice =
  | { kind: 'text'; label: string }
  | { kind: 'coordinates'; label: string; lat: number; lng: number }
  | { kind: 'address'; label: string; magicKey: string }
  | {
      kind: 'feature';
      label: string;
      id: string;
      lat: number;
      lng: number;
    };

/**
 * Column f. A typed phrase can stay as text, or the user can pick an address, a
 * lat/long, or an object from an internal map layer.
 */
export function ReportingLocationField({ bare }: { bare?: boolean }) {
  const { setValue, watch } = useFormContext<ResourceRequestItem>();
  const stored = watch('requested_reporting_location') ?? '';
  const [query, setQuery] = useState(stored);
  const [addresses, setAddresses] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const incidentId = useActualIncidentId();
  const { internalLayerVersionsMap } = useInternalLayersFeatures(incidentId);

  const features = useMemo(() => {
    const rows: LocationChoice[] = [];
    for (const entry of internalLayerVersionsMap.values()) {
      for (const feature of entry.featuresQuery.data ?? []) {
        const point = firstLatLng(feature.geometry);
        if (!feature.name || !point) continue;
        rows.push({
          kind: 'feature',
          label: `${feature.name} · ${entry.internalLayer.name}`,
          id: feature.id,
          lat: point.lat,
          lng: point.lng,
        });
      }
    }
    return rows;
  }, [internalLayerVersionsMap]);

  const trimmed = query.trim();
  const coordinates = parseLatLng(trimmed);
  const featureMatches = trimmed
    ? features
        .filter((feature) =>
          feature.kind === 'feature'
            ? feature.label.toLowerCase().includes(trimmed.toLowerCase())
            : false
        )
        .slice(0, 8)
    : [];

  useEffect(() => {
    if (trimmed.length < 3) return;
    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      void locator
        .suggestLocations(GEOCODE_URL, {
          text: trimmed,
          categories: ['Address', 'Postal', 'Populated Place'],
          location: new Point({
            longitude: -98.5,
            latitude: 39.8,
            spatialReference: WGS84_SPATIAL_REFERENCE,
          }),
        })
        .then((results) => {
          if (cancelled) return;
          setAddresses(
            results
              .flatMap((result) => {
                if (!result.text || !result.magicKey) return [];
                return [{ text: result.text, magicKey: result.magicKey }];
              })
              .slice(0, 5)
          );
        })
        .catch(() => {
          if (!cancelled) setAddresses([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [trimmed]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  /**
   * Typing always stores plain text; geo only applies when a dropdown pick is
   * chosen.
   */
  const applyText = (label: string) => {
    setValue('requested_reporting_location', label, { shouldDirty: true });
    setValue('requested_reporting_location_geolocation', null, {
      shouldDirty: true,
    });
    setValue('requested_reporting_location_id', '', { shouldDirty: true });
    setQuery(label);
  };

  const apply = (choice: LocationChoice) => {
    if (choice.kind === 'text') {
      applyText(choice.label);
    } else if (choice.kind === 'coordinates' || choice.kind === 'feature') {
      setValue('requested_reporting_location', choice.label, {
        shouldDirty: true,
      });
      setValue(
        'requested_reporting_location_geolocation',
        { lat: choice.lat, lng: choice.lng },
        { shouldDirty: true }
      );
      setValue(
        'requested_reporting_location_id',
        choice.kind === 'feature' ? choice.id : '',
        { shouldDirty: true }
      );
      setQuery(choice.label);
    }
    setOpen(false);
  };

  const chooseAddress = async (suggestion: AddressSuggestion) => {
    try {
      const matches = await locator.addressToLocations(GEOCODE_URL, {
        address: { SingleLine: suggestion.text },
        magicKey: suggestion.magicKey,
        maxLocations: 1,
      });
      const location = matches[0]?.location;
      if (location?.latitude == null || location.longitude == null) return;
      apply({
        kind: 'coordinates',
        label: suggestion.text,
        lat: location.latitude,
        lng: location.longitude,
      });
    } catch {
      setOpen(false);
    }
  };

  const choices: LocationChoice[] = [
    ...(trimmed ? [{ kind: 'text' as const, label: trimmed }] : []),
    ...(coordinates
      ? [
          {
            kind: 'coordinates' as const,
            label: `${coordinates.lat}, ${coordinates.lng}`,
            lat: coordinates.lat,
            lng: coordinates.lng,
          },
        ]
      : []),
    ...featureMatches,
  ];

  return (
    <div ref={rootRef} className="tw:relative">
      {bare ? null : (
        <div className="tw:mb-1 tw:text-sm tw:font-medium">
          Requested reporting location
        </div>
      )}
      <Input
        aria-label="Requested reporting location"
        value={query}
        placeholder="Text, address, lat, lng, or map object"
        onChange={(event) => {
          applyText(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && (choices.length > 0 || addresses.length > 0) ? (
        <div className="tw:absolute tw:z-20 tw:mt-1 tw:max-h-56 tw:w-full tw:overflow-y-auto tw:rounded-lg tw:border tw:border-border tw:bg-popover tw:p-1 tw:text-sm tw:shadow-md">
          {choices.map((choice) => (
            <button
              key={
                choice.kind === 'feature'
                  ? `feature-${choice.id}`
                  : `${choice.kind}-${choice.label}`
              }
              type="button"
              className="tw:block tw:w-full tw:rounded-md tw:px-2 tw:py-1 tw:text-left tw:hover:bg-muted"
              onClick={() => apply(choice)}
            >
              {choice.kind === 'text'
                ? `Use “${choice.label}” as text`
                : choice.kind === 'coordinates'
                  ? `Coordinates ${choice.label}`
                  : choice.label}
            </button>
          ))}
          {addresses.map((address) => (
            <button
              key={address.magicKey}
              type="button"
              className="tw:block tw:w-full tw:rounded-md tw:px-2 tw:py-1 tw:text-left tw:hover:bg-muted"
              onClick={() => void chooseAddress(address)}
            >
              {address.text}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
